// src/components/IVR/VariableMenu.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Braces, ChevronDown } from 'lucide-react';
import {
  CALL_CONTEXT_VARIABLES,
  CUSTOM_DATA_EXAMPLES,
  VARIABLE_SOURCE_LABELS,
  asPlaceholder,
  VariableDef,
} from '../../constants/ivrVariables';

/**
 * Dropdown that inserts a {{variable}} placeholder. Purely a picker — the parent
 * decides where the token lands (e.g. at the textarea cursor). Variables are
 * grouped by source and marked when not yet resolvable by the runtime, so the
 * builder is honest about what's live vs. roadmap.
 */
interface VariableMenuProps {
  onInsert: (placeholder: string) => void;
  className?: string;
}

const Group: React.FC<{ title: string; vars: VariableDef[]; onPick: (v: VariableDef) => void }> = ({ title, vars, onPick }) => (
  <div className="py-1">
    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{title}</div>
    {vars.map((v) => (
      <button
        key={v.token}
        type="button"
        onClick={() => onPick(v)}
        className="w-full text-left px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-gray-700 flex items-start gap-2"
        title={v.description}
      >
        <code className="text-xs text-blue-600 dark:text-blue-300 font-mono shrink-0">{asPlaceholder(v.token)}</code>
        <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
          {v.label}{!v.availableNow && <span className="ml-1 text-amber-500">• needs source</span>}
        </span>
      </button>
    ))}
  </div>
);

const VariableMenu: React.FC<VariableMenuProps> = ({ onInsert, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as any)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (v: VariableDef) => { onInsert(asPlaceholder(v.token)); setOpen(false); };

  return (
    <div className={`relative inline-block ${className || ''}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-300 border border-blue-200 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700"
        title="Insert a dynamic variable"
      >
        <Braces className="w-3.5 h-3.5" /> Insert variable <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 max-h-80 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
          <Group title={VARIABLE_SOURCE_LABELS.call_context} vars={CALL_CONTEXT_VARIABLES} onPick={pick} />
          <div className="border-t border-gray-100 dark:border-gray-700" />
          <Group title="Custom data (examples)" vars={CUSTOM_DATA_EXAMPLES} onPick={pick} />
          <div className="px-3 py-2 text-[10px] text-gray-400 border-t border-gray-100 dark:border-gray-700">
            Use <code className="text-blue-500">{'{{user.field}}'}</code> for custom data from an API mapping or provisioned source.
          </div>
        </div>
      )}
    </div>
  );
};

export default VariableMenu;
